<template>

  <v-container class="pa-0 ma-0 receiver-location-selector">

    <!-- Auto-complete finder -->
    <v-row no-gutters align="center" justify="center">
      <v-col cols="12" class="finder-container">
        <v-autocomplete
            clearable
            outlined
            return-object
            :hide-no-data="false"
            hide-selected
            :multiple="multiple"
            :cache-items="false"
            persistent-placeholder
            placeholder="Find by name"
            :hide-details="true"
            :disabled="disabled"
            :label=selectorLabel
            item-text="name"
            item-value="id"
            :items="receiverLocItems"
            :loading="isDataLoading"
            :search-input.sync="search"
            v-model="activeSelection"
            @click:clear="handleClear">

          <!-- List display -->
          <template v-slot:item="data">
            <v-row align="center" @click="handleSelectedItem(data.item)">
              <v-col cols="12" xs="12" sm="8" md="8" lg="8" class="pt-5">
                <div class="select-item-container">
                  <div class="receiver-loc-name">{{ data.item.name }}</div>
                  <div class="mt-1 receiver-loc-address" v-if="showAddress">
                    <div>{{ data.item.street_1 }}</div>
                    <div v-if="data.item.street_2">{{ data.item.street_2 }}</div>
                    <div v-if="generateLocationAddress(data.item).area">{{ generateLocationAddress(data.item).area }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" xs="12" sm="4" md="4" lg="4">
                <v-chip
                    outlined
                    :class="`ml-3 ${data.item.receiver_status}`">
                  {{ data.item.receiver_status }}
                </v-chip>
              </v-col>
            </v-row>
          </template>

          <!-- Selected display -->
          <template v-slot:selection="data">
            <v-chip
                v-bind="data.attrs"
                :input-value="data.selected"
                close
                @click="data.select"
                @click:close="handleRemovedItem(data.item)">
              {{ data.item.name }}
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

  </v-container>

</template>

<script>
export default {
  name: 'ReceiverLocationSelector',

  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    selectorLabel: {
      type: String,
      default: null
    },
    showAddress: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchDebounceMs: {
      type: Number,
      default: 700
    },
    locationsToExclude: {
      type: Array,
      default: null
    }
  },

  data () {
    return {
      search: null,
      activeSearch: null,
      activeSelection: null,
      isDataLoading: false,
      dataLoadingError: null,
      receiverLocData: null
    }
  },

  computed: {
    receiverLocItems () {
      return (this.receiverLocData) ? this.receiverLocData.items : []
    },
    dataMeta () {
      return (this.receiverLocData) ? this.receiverLocData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    }
  },

  watch: {
    search: function (updated) {
      // debouncing search
      if (updated?.length > 0) {
        clearTimeout(this.activeSearch)
        this.activeSearch = setTimeout(() => this.loadData(), this.searchDebounceMs)
      }
    }
  },

  methods: {
    // Emit selected location
    handleSelectedItem (loc) {
      this.$emit('selected', loc)
    },

    // Remove location and emit
    handleRemovedItem (loc) {
      this.activeSelection = this.activeSelection.filter(it => it.id !== loc.id)
      this.$emit('removed', loc)
    },

    // Emit clear event
    handleClear () {
      this.$emit('cleared')
    },

    // Generate multi-line address for display
    generateLocationAddress (loc) {
      return this.$root.utils.getAddressTextFromData(loc, { multi_lines: true })
    },

    async loadData () {
      this.isDataLoading = true

      try {
        // Build request
        const params = {
          with: 'org'
        }

        // Filter by search
        if (this.search) {
          params['name.contains'] = this.search
        }

        // Load receiver location items
        const results = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'receivers',
          opts: { params }
        })

        // Filter out specified locations or return all
        if (this.locationsToExclude) {
          const toInclude = results.items.filter(res => !this.locationsToExclude.some(it => it.id === res.id))
          this.receiverLocData = {
            items: toInclude,
            meta: {
              total_items: toInclude.length
            }
          }
        } else {
          this.receiverLocData = results
        }
      } catch (err) {
        this.dataLoadingError = err.message
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .receiver-location-selector .finder-container {
    width: 100%;
  }

  .receiver-loc-name {
    font-size: 1.1rem;
    font-weight: 500;
  }

  .receiver-loc-address {
    font-size: 0.95rem;
    font-weight: 400;
    color: #9c9c9c;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media screen and (min-width: 600px) {
    .select-item-container {
      min-height: 88px;
    }
  }
</style>
